import React, { FC } from 'react';
import { Tooltip } from 'antd';
import {
	ANSWER_DISPLAY_VARIANT,
	getAnswerDisplayVariant,
	resolveAnswerValues,
	splitScoredAnswer,
} from '../../utils/WalkthroughAnswerDisplayUtils';
import { cn } from '../../utils/ClassNamesUtils';

interface WalkthroughTaskAnswerCellProps {
	taskAnswer: any;
	answerOverride?: {
		numericAnswer: number | null;
		answer: string | null;
		minMultipleChoiceValue?: number | null;
		maxMultipleChoiceValue?: number | null;
	};
	compact?: boolean;
	expanded?: boolean;
}

// Tailwind 3.2 predates the core `line-clamp-*` utilities, so the clamp is spelled out by hand.
export const LINE_CLAMP_STYLE: React.CSSProperties = {
	display: '-webkit-box',
	WebkitLineClamp: 4,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
};

const EmptyAnswer: FC = () => <span className="footnoteregular text-character-disabled">--</span>;

export const WalkthroughTaskAnswerCell: FC<WalkthroughTaskAnswerCellProps> = ({
	taskAnswer,
	answerOverride,
	compact = false,
	expanded = false,
}) => {
	const variant = getAnswerDisplayVariant(taskAnswer);
	const { answer, numericAnswer } = resolveAnswerValues(taskAnswer, answerOverride);

	if (!answer || !String(answer).trim()) return <EmptyAnswer />;

	if (variant === ANSWER_DISPLAY_VARIANT.RESPONSE) {
		return (
			<div
				className="bodyregular w-full break-words"
				style={expanded ? undefined : LINE_CLAMP_STYLE}
			>
				{answer}
			</div>
		);
	}

	if (variant === ANSWER_DISPLAY_VARIANT.UNSCORED) {
		return (
			<Tooltip title={answer}>
				<div
					className={compact ? 'bodyregular w-full' : 'bodyregular w-full'}
					style={LINE_CLAMP_STYLE}
				>
					{answer}
				</div>
			</Tooltip>
		);
	}

	const { value, label } = splitScoredAnswer(answer);

	if (numericAnswer === null || numericAnswer === undefined) {
		return (
			<Tooltip title={answer}>
				<div className="bodyregular w-full truncate text-character-secondary">
					{value === null ? answer : value}
				</div>
			</Tooltip>
		);
	}

	return (
		<Tooltip title={!expanded ? answer : undefined}>
			<div className="w-full text-center">
				<div className="bodymedium text-grey-10">{value === null ? numericAnswer : value}</div>
				{label ? (
					<div
						className={cn(
							'footnoteregular text-character-secondary',
							expanded ? 'break-words' : 'truncate'
						)}
					>
						{label}
					</div>
				) : null}
			</div>
		</Tooltip>
	);
};

export default WalkthroughTaskAnswerCell;
